import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "../../lib/supabase.js";
import { useAuth } from "../../lib/auth.js";
import { Button, Field, Input } from "../../lib/ui.js";
import { SectionCard, errorStyle } from "./common.js";

/**
 * How far from your address you will work (migration 0080). Off by default. When on, the receptionist asks a new customer which suburb
 * the job is in and measures the straight-line distance from your suburb: inside the distance it books as normal, a little beyond it
 * the details come to you as a callback request, and further out the customer is told you do not cover their area. Owners and
 * managers can change it.
 */
export function ServiceAreaSection() {
  const { activeBusinessId } = useAuth();
  const [loaded, setLoaded] = useState(false);
  const [limited, setLimited] = useState(false);
  const [radius, setRadius] = useState("50");
  const [grace, setGrace] = useState("40");
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeBusinessId) return;
    void supabase
      .from("businesses")
      .select("service_radius_km, service_radius_grace_km")
      .eq("id", activeBusinessId)
      .single()
      .then(({ data }: { data: { service_radius_km: number | null; service_radius_grace_km: number } | null }) => {
        if (data) {
          setLimited(data.service_radius_km !== null);
          if (data.service_radius_km !== null) setRadius(String(data.service_radius_km));
          setGrace(String(data.service_radius_grace_km));
        }
        setLoaded(true);
      });
  }, [activeBusinessId]);

  if (!loaded) return null;

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!activeBusinessId) return;
    setError(null);
    setSaved(false);
    const km = Number.parseInt(radius, 10);
    const extra = Number.parseInt(grace, 10);
    if (limited && !(km >= 1 && km <= 2000)) return setError("Enter a distance between 1 and 2000 km.");
    if (!(extra >= 0 && extra <= 2000)) return setError("Enter an extra distance between 0 and 2000 km.");
    const { error: err } = await supabase.rpc("rpc_portal_set_service_area", {
      p_business_id: activeBusinessId,
      p_radius_km: limited ? km : null,
      p_grace_km: extra,
    });
    if (err) {
      setError(
        err.message.includes("business_location_not_found")
          ? "We could not find your suburb and postcode in our list. Check the suburb and postcode in Address above, then save again."
          : err.message,
      );
      return;
    }
    setSaved(true);
  }

  const touch = <T,>(set: (v: T) => void) => (v: T) => {
    setSaved(false);
    set(v);
  };

  return (
    <SectionCard
      title="Service area"
      description="Only take customers within a set distance of your business. The distance is in a straight line from your suburb to the customer's suburb, for calls to the phone receptionist."
    >
      <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 520 }}>
        <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={limited} onChange={(e) => touch(setLimited)(e.target.checked)} />
          <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--rc-text)" }}>Limit how far I travel</span>
        </label>
        {limited && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Field label="Serve customers within (km)">
              <Input value={radius} inputMode="numeric" maxLength={4} onChange={(e) => touch(setRadius)(e.target.value)} style={{ width: 110 }} />
            </Field>
            <Field label="Ask me about jobs up to (km) further">
              <Input value={grace} inputMode="numeric" maxLength={4} onChange={(e) => touch(setGrace)(e.target.value)} style={{ width: 110 }} />
            </Field>
          </div>
        )}
        {limited && (
          <p style={{ fontSize: 12, color: "var(--rc-text-muted)", margin: 0 }}>
            Within {radius || "…"} km the receptionist books as normal. Between {radius || "…"} and {(Number.parseInt(radius, 10) || 0) + (Number.parseInt(grace, 10) || 0)} km
            the receptionist takes the customer's details for you to review and call back. Further out it politely says you do not cover that area.
          </p>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Button type="submit" variant="primary">
            Save service area
          </Button>
          {saved && <span style={{ fontSize: 12.5, color: "var(--rc-success)" }}>Saved. Takes effect within a minute.</span>}
        </div>
      </form>
      {error && <p style={errorStyle}>{error}</p>}
    </SectionCard>
  );
}
